import { diagLog, diagWarn } from "@/diagnostics/freezeDiagnostics";

const WEBP_QUALITY = 0.90;
const THUMBNAIL_WEBP_QUALITY = 0.75;

/** Lado maior máximo do bitmap/canvas de processamento. Não amplia imagens menores. */
export const MAX_PROCESSING_EDGE = 8192;

/** Lado maior máximo do thumbnail persistido. Não amplia imagens menores. */
export const MAX_THUMBNAIL_EDGE = 1200;

export { WEBP_QUALITY as PANORAMA_WEBP_QUALITY, THUMBNAIL_WEBP_QUALITY };

/**
 * @param {number} width
 * @param {number} height
 * @returns {number}
 */
export function rgbaBytes(width, height) {
  return width * height * 4;
}

/**
 * @param {number} width
 * @param {number} height
 * @returns {number}
 */
export function rgbaMegabytes(width, height) {
  return Math.round(rgbaBytes(width, height) / 1048576);
}

/**
 * Calcula o tamanho de processamento preservando a proporção.
 * Imagens cujo lado maior já é ≤ maxEdge não são ampliadas.
 *
 * @param {number} sourceWidth
 * @param {number} sourceHeight
 * @param {number} [maxEdge]
 * @returns {{ width: number, height: number, scale: number }}
 */
export function getProcessingTargetSize(
  sourceWidth,
  sourceHeight,
  maxEdge = MAX_PROCESSING_EDGE,
) {
  const width = Math.round(Number(sourceWidth));
  const height = Math.round(Number(sourceHeight));

  if (!Number.isFinite(width) || !Number.isFinite(height) || width < 1 || height < 1) {
    throw new Error("Dimensões de imagem inválidas.");
  }

  const longest = Math.max(width, height);
  if (longest <= maxEdge) {
    return { width, height, scale: 1 };
  }

  const scale = maxEdge / longest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
    scale,
  };
}

function readUint24Le(view, offset) {
  return (
    view.getUint8(offset) |
    (view.getUint8(offset + 1) << 8) |
    (view.getUint8(offset + 2) << 16)
  );
}

function readPngSize(view) {
  if (view.byteLength < 24) {
    return null;
  }
  if (view.getUint32(0) !== 0x89504e47 || view.getUint32(4) !== 0x0d0a1a0a) {
    return null;
  }
  const width = view.getUint32(16);
  const height = view.getUint32(20);
  if (width < 1 || height < 1) {
    return null;
  }
  return { width, height };
}

function readJpegSize(view) {
  if (view.byteLength < 4 || view.getUint16(0) !== 0xffd8) {
    return null;
  }

  let offset = 2;
  while (offset + 8 < view.byteLength) {
    if (view.getUint8(offset) !== 0xff) {
      offset += 1;
      continue;
    }

    const marker = view.getUint8(offset + 1);
    if (marker === 0x00 || marker === 0xff) {
      offset += 1;
      continue;
    }
    if (marker === 0xd8) {
      offset += 2;
      continue;
    }
    if (marker === 0xd9 || marker === 0xda) {
      break;
    }
    if (marker >= 0xd0 && marker <= 0xd7) {
      offset += 2;
      continue;
    }

    if (offset + 4 > view.byteLength) {
      break;
    }

    const length = view.getUint16(offset + 2);
    const isSof =
      (marker >= 0xc0 && marker <= 0xc3) ||
      (marker >= 0xc5 && marker <= 0xc7) ||
      (marker >= 0xc9 && marker <= 0xcb) ||
      (marker >= 0xcd && marker <= 0xcf);

    if (isSof && length >= 7 && offset + 8 < view.byteLength) {
      const height = view.getUint16(offset + 5);
      const width = view.getUint16(offset + 7);
      if (width > 0 && height > 0) {
        return { width, height };
      }
    }

    if (length < 2) {
      break;
    }
    offset += 2 + length;
  }

  return null;
}

function readWebpSize(view) {
  if (view.byteLength < 30) {
    return null;
  }

  const riff = String.fromCharCode(
    view.getUint8(0),
    view.getUint8(1),
    view.getUint8(2),
    view.getUint8(3),
  );
  const webp = String.fromCharCode(
    view.getUint8(8),
    view.getUint8(9),
    view.getUint8(10),
    view.getUint8(11),
  );
  if (riff !== "RIFF" || webp !== "WEBP") {
    return null;
  }

  const fourcc = String.fromCharCode(
    view.getUint8(12),
    view.getUint8(13),
    view.getUint8(14),
    view.getUint8(15),
  );

  if (fourcc === "VP8X" && view.byteLength >= 30) {
    return {
      width: readUint24Le(view, 24) + 1,
      height: readUint24Le(view, 27) + 1,
    };
  }

  if (fourcc === "VP8 " && view.byteLength >= 40) {
    const payload = 20;
    if (
      view.getUint8(payload + 3) === 0x9d &&
      view.getUint8(payload + 4) === 0x01 &&
      view.getUint8(payload + 5) === 0x2a
    ) {
      return {
        width: view.getUint16(payload + 6, true) & 0x3fff,
        height: view.getUint16(payload + 8, true) & 0x3fff,
      };
    }
  }

  if (fourcc === "VP8L" && view.byteLength >= 25 && view.getUint8(20) === 0x2f) {
    const bits = view.getUint32(21, true);
    return {
      width: (bits & 0x3fff) + 1,
      height: ((bits >> 14) & 0x3fff) + 1,
    };
  }

  return null;
}

/**
 * Lê largura/altura a partir do cabeçalho, sem decodificar o bitmap.
 *
 * @param {ArrayBuffer | ArrayBufferView} bytes
 * @returns {{ width: number, height: number } | null}
 */
export function readImageSizeFromBytes(bytes) {
  const buffer = bytes instanceof ArrayBuffer ? bytes : bytes.buffer;
  const view = new DataView(
    buffer,
    bytes.byteOffset ?? 0,
    bytes.byteLength ?? buffer.byteLength,
  );

  return readPngSize(view) || readJpegSize(view) || readWebpSize(view);
}

/**
 * @param {Blob} blob
 * @returns {Promise<ArrayBuffer>}
 */
async function readBlobBytes(blob) {
  if (typeof blob.arrayBuffer === "function") {
    return blob.arrayBuffer();
  }

  if (typeof FileReader !== "undefined") {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error || new Error("Falha ao ler o arquivo."));
      reader.readAsArrayBuffer(blob);
    });
  }

  return new Response(blob).arrayBuffer();
}

/**
 * @param {Blob} file
 * @returns {Promise<{ width: number, height: number, via: "header" | "bitmap-fallback" }>}
 */
export async function getSourceImageSize(file) {
  const headerSlice =
    typeof file.slice === "function" ? file.slice(0, 512 * 1024) : file;
  const headerBuffer = await readBlobBytes(headerSlice);
  const fromHeader = readImageSizeFromBytes(headerBuffer);
  if (fromHeader) {
    return { ...fromHeader, via: "header" };
  }

  const probe = await createImageBitmap(file);
  try {
    return {
      width: probe.width,
      height: probe.height,
      via: "bitmap-fallback",
    };
  } finally {
    probe.close?.();
  }
}

function releaseBitmap(bitmap) {
  try {
    bitmap?.close?.();
  } catch {
    // ignore
  }
}

function releaseCanvas(canvas) {
  if (!canvas) {
    return;
  }
  canvas.width = 0;
  canvas.height = 0;
}

/**
 * @param {Blob} file
 * @param {number} targetWidth
 * @param {number} targetHeight
 * @param {number} sourceWidth
 * @param {number} sourceHeight
 * @returns {Promise<{ bitmap: ImageBitmap, resizedInBitmap: boolean }>}
 */
async function createProcessingBitmap(
  file,
  targetWidth,
  targetHeight,
  sourceWidth,
  sourceHeight,
) {
  const needsResize =
    targetWidth !== sourceWidth || targetHeight !== sourceHeight;

  if (!needsResize) {
    return {
      bitmap: await createImageBitmap(file),
      resizedInBitmap: false,
    };
  }

  try {
    const bitmap = await createImageBitmap(file, {
      resizeWidth: targetWidth,
      resizeHeight: targetHeight,
      resizeQuality: "high",
    });

    if (bitmap.width === targetWidth && bitmap.height === targetHeight) {
      return { bitmap, resizedInBitmap: true };
    }

    diagWarn("convert bitmap-resize size mismatch", {
      expected: `${targetWidth}x${targetHeight}`,
      received: `${bitmap.width}x${bitmap.height}`,
    });
    releaseBitmap(bitmap);
  } catch (error) {
    diagWarn(
      "convert bitmap-resize unsupported",
      String(error?.message || error),
    );
  }

  return {
    bitmap: await createImageBitmap(file),
    resizedInBitmap: false,
  };
}

/**
 * @typedef {Object} EncodedImageResult
 * @property {Blob} processedBlob
 * @property {number} width
 * @property {number} height
 * @property {number} sourceWidth
 * @property {number} sourceHeight
 * @property {boolean} resizedInBitmap
 */

/**
 * Encoda um Blob/File para WebP com teto de lado maior, liberando bitmap/canvas
 * imediatamente após o encode. Não amplia imagens menores que o teto.
 *
 * @param {Blob} source
 * @param {{
 *   maxEdge: number,
 *   quality: number,
 *   knownWidth?: number,
 *   knownHeight?: number,
 *   logPrefix?: string,
 * }} options
 * @returns {Promise<EncodedImageResult>}
 */
export async function encodeImageToWebp(source, options) {
  const {
    maxEdge,
    quality,
    knownWidth,
    knownHeight,
    logPrefix = "convert",
  } = options;
  const startedAt = performance.now();
  let bitmap = null;
  let canvas = null;

  try {
    let sourceWidth = knownWidth;
    let sourceHeight = knownHeight;
    let via = "provided";

    if (!sourceWidth || !sourceHeight) {
      const sourceSize = await getSourceImageSize(source);
      sourceWidth = sourceSize.width;
      sourceHeight = sourceSize.height;
      via = sourceSize.via;
    }

    const target = getProcessingTargetSize(sourceWidth, sourceHeight, maxEdge);

    diagLog(`${logPrefix} source`, {
      sourceWidth,
      sourceHeight,
      sourceRGBA: rgbaMegabytes(sourceWidth, sourceHeight),
      via,
    });
    diagLog(`${logPrefix} target`, {
      targetWidth: target.width,
      targetHeight: target.height,
      targetRGBA: rgbaMegabytes(target.width, target.height),
    });

    const bitmapStartedAt = performance.now();
    const created = await createProcessingBitmap(
      source,
      target.width,
      target.height,
      sourceWidth,
      sourceHeight,
    );
    bitmap = created.bitmap;
    const bitmapMs = Math.round(performance.now() - bitmapStartedAt);

    diagLog(`${logPrefix} bitmap`, {
      bitmapWidth: bitmap.width,
      bitmapHeight: bitmap.height,
      bitmapMs,
      resizedInBitmap: created.resizedInBitmap,
      rgbaMB: rgbaMegabytes(bitmap.width, bitmap.height),
    });

    canvas = document.createElement("canvas");
    canvas.width = target.width;
    canvas.height = target.height;

    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Não foi possível processar a imagem.");
    }

    const drawStartedAt = performance.now();
    context.drawImage(bitmap, 0, 0, target.width, target.height);
    releaseBitmap(bitmap);
    bitmap = null;
    const drawMs = Math.round(performance.now() - drawStartedAt);
    diagLog(`${logPrefix} draw`, { drawMs });

    const blobStartedAt = performance.now();
    const processedBlob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
            return;
          }
          reject(new Error("Falha na conversão para WEBP."));
        },
        "image/webp",
        quality,
      );
    });
    const toBlobMs = Math.round(performance.now() - blobStartedAt);

    releaseCanvas(canvas);
    diagLog(`${logPrefix} encode`, {
      toBlobMs,
      quality,
      blobBytes: processedBlob.size,
      canvasAfterRelease: `${canvas.width}x${canvas.height}`,
    });
    diagLog(`${logPrefix} size`, {
      width: target.width,
      height: target.height,
      blobBytes: processedBlob.size,
    });

    const totalMs = Math.round(performance.now() - startedAt);
    diagLog(`${logPrefix} done`, {
      sourceWidth,
      sourceHeight,
      targetWidth: target.width,
      targetHeight: target.height,
      bitmapMs,
      drawMs,
      toBlobMs,
      totalMs,
      resizedInBitmap: created.resizedInBitmap,
      blobBytes: processedBlob.size,
    });

    return {
      processedBlob,
      width: target.width,
      height: target.height,
      sourceWidth,
      sourceHeight,
      resizedInBitmap: created.resizedInBitmap,
    };
  } catch (error) {
    releaseBitmap(bitmap);
    releaseCanvas(canvas);
    throw error;
  }
}

/**
 * Gera o thumbnail a partir do panorama já processado (≤8192), evitando um
 * segundo decode do arquivo original. Recursos grandes do panorama já devem
 * ter sido liberados pelo caller.
 *
 * @param {Blob} processedPanoramaBlob
 * @param {number} panoramaWidth
 * @param {number} panoramaHeight
 * @returns {Promise<EncodedImageResult>}
 */
export async function createThumbnailBlob(
  processedPanoramaBlob,
  panoramaWidth,
  panoramaHeight,
) {
  return encodeImageToWebp(processedPanoramaBlob, {
    maxEdge: MAX_THUMBNAIL_EDGE,
    quality: THUMBNAIL_WEBP_QUALITY,
    knownWidth: panoramaWidth,
    knownHeight: panoramaHeight,
    logPrefix: "thumbnail",
  });
}

/**
 * Processa o arquivo com o pipeline local (Canvas → WebP):
 * 1. panorama (lado maior ≤8192, WebP 0.90) → libera bitmap/canvas
 * 2. thumbnail a partir do panorama processado (lado maior ≤1200, WebP 0.75)
 *
 * @param {File | Blob} file
 * @returns {Promise<{
 *   processedBlob: Blob,
 *   thumbnailBlob: Blob,
 *   width: number,
 *   height: number,
 *   sourceWidth: number,
 *   sourceHeight: number,
 * }>}
 */
export async function processImageForUpload(file) {
  const startedAt = performance.now();

  diagLog("convert start", {
    sizeBytes: file?.size,
    type: file?.type,
  });

  const panorama = await encodeImageToWebp(file, {
    maxEdge: MAX_PROCESSING_EDGE,
    quality: WEBP_QUALITY,
    logPrefix: "convert",
  });

  const thumbnail = await createThumbnailBlob(
    panorama.processedBlob,
    panorama.width,
    panorama.height,
  );

  diagLog("convert done", {
    sourceWidth: panorama.sourceWidth,
    sourceHeight: panorama.sourceHeight,
    targetWidth: panorama.width,
    targetHeight: panorama.height,
    thumbnailWidth: thumbnail.width,
    thumbnailHeight: thumbnail.height,
    panoramaBytes: panorama.processedBlob.size,
    thumbnailBytes: thumbnail.processedBlob.size,
    totalMs: Math.round(performance.now() - startedAt),
  });

  return {
    processedBlob: panorama.processedBlob,
    thumbnailBlob: thumbnail.processedBlob,
    width: panorama.width,
    height: panorama.height,
    sourceWidth: panorama.sourceWidth,
    sourceHeight: panorama.sourceHeight,
  };
}

/**
 * Converte um arquivo de imagem para WEBP via Canvas API (panorama).
 *
 * @param {File | Blob} file
 * @returns {Promise<Blob>}
 */
export async function convertToWebp(file) {
  const { processedBlob } = await processImageForUpload(file);
  return processedBlob;
}
